/**
 * HARUM customizations (HAR-103 ~ HAR-109).
 * Custom elements that react to Hyper's variant change flow:
 * product-info.js publishes FoxTheme.pubsub 'variant-change' with
 * { data: { sectionId, html (parsed section document), variant } }.
 */

/* Shows itself only while the selected variant is out of stock (HAR-106/107). */
if (!customElements.get('harum-oos-toggle')) {
  customElements.define(
    'harum-oos-toggle',
    class HarumOosToggle extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          ({ data: { sectionId, variant } }) => {
            if (sectionId !== this.dataset.section) return;
            const show = !!variant && variant.available === false;
            this.classList.toggle('hidden', !show);
            if (show) this.dispatchEvent(new CustomEvent('harum:oos-shown'));
          }
        );
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }
    }
  );
}

/* Re-syncs its innerHTML from the freshly rendered section on variant change (HAR-105). */
if (!customElements.get('harum-variant-sync')) {
  customElements.define(
    'harum-variant-sync',
    class HarumVariantSync extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          ({ data: { sectionId, html } }) => {
            if (sectionId !== this.dataset.section || !this.id || !html) return;
            const source = html.getElementById(this.id);
            if (!source) return;
            this.innerHTML = source.innerHTML;
            this.classList.toggle('hidden', source.classList.contains('hidden'));
          }
        );
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }
    }
  );
}

/* Fetches external marketplace listings once, lazily (HAR-107). */
if (!customElements.get('harum-marketplace')) {
  customElements.define(
    'harum-marketplace',
    class HarumMarketplace extends HTMLElement {
      connectedCallback() {
        this.loaded = false;
        const toggle = this.closest('harum-oos-toggle');
        if (toggle && toggle.classList.contains('hidden')) {
          this.onShown = () => this.load();
          toggle.addEventListener('harum:oos-shown', this.onShown);
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        const toggle = this.closest('harum-oos-toggle');
        if (toggle && this.onShown) toggle.removeEventListener('harum:oos-shown', this.onShown);
      }

      async load() {
        if (this.loaded) return;
        this.loaded = true;

        const endpoint = this.dataset.endpoint;
        const query = (this.dataset.query || '').trim();
        const max = parseInt(this.dataset.max, 10) || 4;
        if (!endpoint || !query) {
          this.hideWidget();
          return;
        }

        // HAR-248: a product title is not sent as-is. The search API matches loosely and
        // ranks poorly ("Cortis: GREENGREEN - The 2nd EP Playextended Ball ver." put two
        // photocard binders first), and for a title it knows nothing about it still
        // returns a page of unrelated items. So the title is split into its distinctive
        // words, two or three short queries are run, the results are merged, ranked by how
        // many of the title's words each listing contains, and listings that share none of
        // the artist / series words are dropped. Nothing relevant -> widget hidden.
        if (this.dataset.querySource === 'product_title') {
          const plan = HarumMarketplace.titleSearchPlan(query);
          const results = await Promise.all(plan.queries.map((q) => this.search(endpoint, q)));
          const ranked = HarumMarketplace.rankByTitle(results, plan);
          if (ranked.length === 0) {
            this.hideWidget();
            return;
          }
          this.renderItems(ranked.slice(0, max));
          return;
        }

        // Collection title / custom query: one request, shown as returned (HAR-107 behaviour).
        const items = await this.search(endpoint, query);
        if (items.length === 0) {
          this.hideWidget();
          return;
        }
        this.renderItems(items.slice(0, max));
      }

      /* One search request. A network or HTTP failure counts as "no results". */
      async search(endpoint, query) {
        try {
          // marketplace.harum.io contract: POST { singleQuery, locale } ->
          // { items: [{ title, image, urlSlug, productId, harumId, pricing: { fullPriceText } }] }
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              singleQuery: query,
              locale: this.dataset.locale || 'en-US',
            }),
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const results = await response.json();
          const items = results && results.items;
          return Array.isArray(items) ? items : [];
        } catch (error) {
          console.warn('[harum-marketplace] search failed for', JSON.stringify(query), error);
          return [];
        }
      }

      /* Lower-cased words of a string, letters and digits only. */
      static words(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim()
          .split(' ')
          .filter(Boolean);
      }

      /* HAR-248: what to search for a product title, and which words to rank by.
         Titles in this store read "Artist: Series - Item (variant)". The part before
         " - " names the product line (head), the rest describes the item (tail).
           "Cortis: GREENGREEN - The 2nd EP Playextended Ball ver. (more in marketplace https://…)"
             queries    -> "Cortis: GREENGREEN EP Playextended Ball", "Cortis: GREENGREEN Ball", "Cortis: GREENGREEN"
             headTokens -> ["cortis", "greengreen"]      (ranking, weight 2)
             tailTokens -> ["ep", "playextended", "ball"] (ranking, weight 1)
         The head is sent as written: the API matches by meaning, and "Our Guild Idol
         Pop-Up" finds that webtoon's goods while "guild idol pop" finds nothing. Only
         bracketed tails, links, filler words and ordinals ("The", "ver.", "2nd") are
         dropped, since they dilute the search. */
      static titleSearchPlan(title) {
        const FILLER = new Set([
          'the', 'a', 'an', 'of', 'and', 'or', 'x', 'in', 'on', 'to', 'for', 'with', 'our', 'up',
          'ver', 'version', 'edition', 'ed', 'random', 'type', 'types', 'more', 'marketplace',
        ]);
        const keep = (word) => word.length >= 2 && !FILLER.has(word) && !/^\d+(st|nd|rd|th)?$/.test(word);
        const unique = (list) => list.filter((entry, i) => list.indexOf(entry) === i);
        const tidy = (value) => value.replace(/\s+/g, ' ').replace(/[\s\-–—:;,|/.]+$/g, '').trim();

        const cleaned = String(title || '')
          .replace(/\s*[\(\[][^\)\]]*[\)\]]/g, ' ')
          .replace(/https?:\/\/\S+/gi, ' ');
        const parts = cleaned.split(/\s[-–—]\s/);
        let headPhrase = tidy(parts[0]);
        // Tail words keep their spelling but lose filler: "The 2nd EP Playextended Ball ver." -> "EP Playextended Ball".
        let tailWords = parts
          .slice(1)
          .join(' ')
          .split(/[^\p{L}\p{N}]+/u)
          .filter((word) => word && keep(word.toLowerCase()));
        if (!headPhrase) {
          headPhrase = tidy(tailWords.join(' ')) || tidy(cleaned) || tidy(String(title || ''));
          tailWords = [];
        }

        let headTokens = unique(HarumMarketplace.words(headPhrase).filter(keep));
        if (headTokens.length === 0) headTokens = unique(HarumMarketplace.words(headPhrase));
        const tailTokens = unique(tailWords.map((word) => word.toLowerCase())).filter(
          (word) => !headTokens.includes(word)
        );

        const queries = unique(
          [
            tailWords.length ? `${headPhrase} ${tailWords.join(' ')}` : '',
            tailWords.length ? `${headPhrase} ${tailWords[tailWords.length - 1]}` : '',
            headPhrase,
          ]
            .map(tidy)
            .filter(Boolean)
        );
        return { headTokens, tailTokens, queries };
      }

      /* HAR-248: merge several result lists, score each listing by the title words it
         contains (head words count double; a word of 5+ letters also matches inside a
         longer word, so "greengreen" finds "Green Green"), keep only listings that are
         about the same product line - two head words, or one head word plus one item
         word ("Cortis" + "Ball") - and order by score, then by the API's own order.
         The API returns loosely similar items for anything it does not know, so a
         listing that merely shares one generic word ("Market") is not enough. */
      static rankByTitle(resultLists, plan) {
        const seen = new Set();
        const merged = [];
        resultLists.forEach((list) => {
          (list || []).forEach((item) => {
            if (!item) return;
            const key = item.harumId || item.productId || item.urlSlug || item.title;
            if (!key || seen.has(key)) return;
            seen.add(key);
            merged.push(item);
          });
        });

        const scored = merged.map((item, index) => {
          const itemWords = HarumMarketplace.words(item.title);
          const spaced = ` ${itemWords.join(' ')} `;
          const compact = itemWords.join('');
          const has = (word) => spaced.includes(` ${word} `) || (word.length >= 5 && compact.includes(word));
          let headHits = 0;
          let tailHits = 0;
          plan.headTokens.forEach((word) => {
            if (has(word)) headHits += 1;
          });
          plan.tailTokens.forEach((word) => {
            if (has(word)) tailHits += 1;
          });
          return { item, index, headHits, tailHits, score: headHits * 2 + tailHits };
        });

        const neededHeadHits = Math.min(2, plan.headTokens.length);
        return scored
          .filter((entry) => entry.headHits >= neededHeadHits || (entry.headHits > 0 && entry.tailHits > 0))
          .sort((a, b) => b.score - a.score || a.index - b.index)
          .map((entry) => entry.item);
      }

      /* Builds cards with DOM APIs only - external data never goes through innerHTML. */
      renderItems(items) {
        const grid = this.querySelector('.harum-marketplace__grid');
        if (!grid) return;
        const buttonLabel = this.dataset.buttonLabel || 'View item';
        // Listing pages live on the endpoint's origin under /listings/<urlSlug>.
        // (item.link is empty for Bunjang-sourced items - build the URL ourselves.)
        let listingsBase = '';
        try {
          listingsBase = `${new URL(this.dataset.endpoint).origin}/listings/`;
        } catch (e) {
          /* relative or invalid endpoint - fall back to item.link */
        }

        items.forEach((item) => {
          if (!item) return;
          const slug = item.urlSlug || item.productId;
          const href = item.link || (listingsBase && slug ? `${listingsBase}${encodeURIComponent(slug)}` : '');
          if (!href) return;

          const card = document.createElement('a');
          card.className = 'harum-marketplace__card flex flex-col';
          card.href = href;
          card.target = '_blank';
          card.rel = 'noopener nofollow';

          const media = document.createElement('div');
          media.className = 'harum-marketplace__card-media blocks-radius';
          if (item.image) {
            const img = document.createElement('img');
            img.src = item.image;
            img.alt = item.title || '';
            img.loading = 'lazy';
            media.appendChild(img);
          }
          card.appendChild(media);

          const title = document.createElement('p');
          title.className = 'harum-marketplace__card-title';
          title.textContent = (item.title || '').replace(/<[^>]*>/g, '');
          card.appendChild(title);

          const priceText =
            (item.pricing && (item.pricing.fullPriceText || item.pricing.priceText)) ||
            (item.price ? `$${item.price} USD` : '');
          if (priceText) {
            const price = document.createElement('span');
            price.className = 'harum-marketplace__card-price f-price__regular';
            price.textContent = priceText;
            card.appendChild(price);
          }

          const button = document.createElement('span');
          button.className = 'btn btn--primary btn--small harum-marketplace__card-btn';
          button.textContent = buttonLabel;
          card.appendChild(button);

          grid.appendChild(card);
        });

        this.classList.remove('harum-marketplace--loading');
      }

      hideWidget() {
        this.classList.add('hidden');
      }
    }
  );
}

/* HAR-132: move the "Frequently Bought Together" widget above the reviews.
   The FBT app (Code Black Belt, window.codeblackbelt) injects asynchronously
   and, on this store, renders the widget inside the FOOTER (so it lands below
   the product reviews). We watch for it and relocate the whole widget to just
   before the reviews section.

   CBB splits its markup: the titled block is `.cbb-frequently-bought-container`
   ("Related Item" + products + total) while the submit button lives separately
   as `.cbb-frequently-bought-add-button`. We move the container(s) and carry the
   detached button with them. Only CBB elements are moved (never a theme section).
   If the app markup changes, update the CBB_* selectors below. */
(function () {
  if (window.harumFbtReorderInit) return;
  window.harumFbtReorderInit = true;

  var CBB_CONTAINER = '.cbb-frequently-bought-container';
  var CBB_BUTTON = '.cbb-frequently-bought-add-button';

  function reviewsAnchor() {
    return (
      document.querySelector('[id*="rich_text_btDic4"]') ||
      document.querySelector('[id*="178220296253d08b8e"]') ||
      document.querySelector('.shopify-section [id*="loox_card_carousel"], .shopify-section [class*="loox-reviews"]')
    );
  }

  function relocate() {
    var anchor = reviewsAnchor();
    if (!anchor) return;
    var containers = document.querySelectorAll(CBB_CONTAINER);
    if (!containers.length) return;

    // Reuse or create a page-width--fixed wrapper right before the reviews
    // section. Using the theme's own container class makes the widget line up
    // with the other sections (e.g. "Recently viewed products") at every width.
    var wrap = document.getElementById('harum-fbt-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = 'harum-fbt-wrap';
      wrap.className = 'page-width page-width--fixed harum-fbt-wrap';
    }
    if (wrap.parentNode !== anchor.parentNode || wrap.nextElementSibling !== anchor) {
      anchor.parentNode.insertBefore(wrap, anchor);
    }

    containers.forEach(function (container) {
      if (container.parentNode !== wrap) {
        wrap.appendChild(container);
      }
      // The submit button can render outside the container; keep it with the
      // widget if it ended up elsewhere.
      var btn = document.querySelector(CBB_BUTTON);
      if (btn && !wrap.contains(btn)) {
        wrap.appendChild(btn);
      }
    });
  }

  function start() {
    relocate();
    var observer = new MutationObserver(function () {
      relocate();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Stop watching after 20s so we never observe forever.
    setTimeout(function () {
      observer.disconnect();
    }, 20000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
